import beerIcon from "../assets/page_logo.png";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { authApi } from "../services/api";
import { tokenStore } from "../services/authClient";
import { toast } from "react-toastify";
import VerifyEmailCta from "../components/VerifyEmailCta";
import { PrivacyPolicyContent } from "../components/privacyPolicy";

function AdminSignup() {
  // Signup flow states
  const [signupOtpMode, setSignupOtpMode] = useState(false); // Track OTP verification step
  // Set when signup is refused because the address is taken. The toast alone was a dead
  // end: the partner most likely to see this is one whose own unverified signup owns the
  // address, and the fix is to verify it, not to pick a different email.
  // Which "email already taken" case the backend reported, so the two are routed
  // differently: null | 'unverified' (rescue at /verify-email) | 'registered' (log in).
  const [existingAccount, setExistingAccount] = useState(null);
  const [email, setEmail] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [signupOtp, setSignupOtp] = useState(["", "", "", "", "", ""]); // OTP for signup flow
  const [signupResendCooldown, setSignupResendCooldown] = useState(true);
  const signupInputRefs = useRef([]);
  const [signupCooldownTime, setSignupCooldownTime] = useState(60);

  // Legal document modal states
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  // Cooldown for signup OTP resend
  useEffect(() => {
    if (signupOtpMode) {
      console.log(
        "🕒 [Frontend] Signup OTP cooldown started — 'Send again' disabled",
      );
      setSignupResendCooldown(true);
      setSignupCooldownTime(60);

      const interval = setInterval(() => {
        setSignupCooldownTime((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setSignupResendCooldown(false);
            console.log(
              "✅ [Frontend] Signup cooldown finished — resend enabled",
            );
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [signupOtpMode]);

  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // setIsValid(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value));
    setIsValid(/^[\w.+-]+@([\w-]+\.)+[\w-]{2,}$/i.test(value));
  };

  // Legal Document Modal Component
  const LegalModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
          padding: "20px",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            maxWidth: "900px",
            width: "100%",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div
            style={{
              padding: "20px 30px",
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#F9F4EA",
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: "#000",
              }}
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#666",
                padding: "0",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>

          {/* Modal Content */}
          <div
            style={{
              padding: "30px",
              overflowY: "auto",
              flex: 1,
            }}
          >
            {children}
          </div>

          {/* Modal Footer */}
          <div
            style={{
              padding: "20px 30px",
              borderTop: "1px solid #eee",
              display: "flex",
              justifyContent: "flex-end",
              backgroundColor: "#F9F4EA",
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "10px 24px",
                borderRadius: "30px",
                border: "none",
                cursor: "pointer",
                background: "#DE810E",
                color: "white",
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Terms of Service Content Component
  const TermsContent = () => {
    const OrangeBullet = ({ title, children }) => (
      <article style={{ marginBottom: "20px" }}>
        <p
          style={{
            fontWeight: "bold",
            fontSize: "1rem",
            color: "#ff8c00",
            marginTop: "20px",
          }}
        >
          → {title}
        </p>
        <p
          style={{
            fontSize: "1rem",
            lineHeight: "1.8",
            marginBottom: "10px",
            color: "#444",
          }}
        >
          {children}
        </p>
      </article>
    );

    const Divider = () => (
      <hr
        style={{
          border: "none",
          borderTop: "1px solid #DE810E",
          margin: "30px 0",
        }}
      />
    );

    return (
      <div>
        <p style={{ fontSize: "1rem", color: "#555" }}>
          <strong>Effective Date:</strong> August 7, 2025
        </p>
        <p style={{ fontSize: "1rem", color: "#555", marginBottom: "20px" }}>
          <strong>Last Updated:</strong> August 7, 2025
        </p>

        <p
          style={{
            fontSize: "1rem",
            lineHeight: "1.8",
            color: "#444",
            marginBottom: "30px",
          }}
        >
          Welcome to my HOOM. These Terms of Service ("Terms") govern your
          access to and use of our mobile application and related services (the
          "Service"). By accessing or using the Service, you agree to be bound
          by these Terms.
        </p>

        <OrangeBullet title="1. Eligibility">
          You must be at least 21 years old and legally allowed to purchase
          alcohol in your jurisdiction to use this app. By using the Service,
          you represent and warrant that you meet these requirements.
        </OrangeBullet>

        <OrangeBullet title="2. Service Overview">
          my HOOM enables users to purchase alcoholic beverages (e.g., beer) for
          other individuals ("Recipients") through participating, licensed
          vendors. We do not sell or deliver alcohol ourselves—we are a platform
          facilitating transactions between users and verified vendors.
        </OrangeBullet>

        <OrangeBullet title="3. Account Registration">
          To use the Service, you must create an account, provide accurate
          personal information, and consent to identity and age verification
          processes.
        </OrangeBullet>

        <OrangeBullet title="4. Age Verification">
          Recipients must be 21+ and will be required to verify their age and
          identity upon redemption of a gift. Failure to do so will result in
          cancellation without refund.
        </OrangeBullet>

        <Divider />

        <OrangeBullet title="5. Acceptable Use">You agree not to:</OrangeBullet>

        <div style={{ marginLeft: "20px", marginBottom: "20px" }}>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● Violate any local, state, or federal law regarding alcohol use or
            transfer
          </p>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● Impersonate others or use fraudulent payment methods
          </p>
          <p style={{ fontSize: "1rem", lineHeight: "1.8", color: "#444" }}>
            ● Harass, exploit, or abuse other users or vendors
          </p>
        </div>

        <OrangeBullet title="6. Payments & Refunds">
          All purchases are final once accepted by a vendor. Refunds will not be
          provided unless required by law or in the event of vendor failure. We
          use third-party payment processors; you agree to their terms as well.
        </OrangeBullet>

        <OrangeBullet title="7. Third-Party Vendors">
          We are not responsible for the acts, omissions, or product quality of
          third-party vendors. All liability for the sale and service of alcohol
          lies with the licensed seller.
        </OrangeBullet>

        <Divider />

        <OrangeBullet title="8. SMS & Text Messaging Consent (Opt-In Required)">
          By providing your mobile phone number and explicitly opting in, you
          consent to receive text messages (SMS/MMS) from my HOOM.
        </OrangeBullet>

        <div style={{ marginLeft: "20px", marginBottom: "20px" }}>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● Your consent to receive text messages is voluntary and is not a
            condition of purchasing or using the Service.
          </p>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● Messaging consent is separate from acceptance of these Terms of
            Service and our Privacy Policy.
          </p>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● You will only receive text messages if you clearly affirm consent,
            such as by selecting a checkbox or tapping a button stating: "I
            agree to receive text messages from my HOOM."
          </p>
        </div>

        <p
          style={{
            fontSize: "1rem",
            lineHeight: "1.8",
            color: "#444",
            marginBottom: "10px",
            marginLeft: "20px",
          }}
        >
          Types of messages may include, but are not limited to:
        </p>

        <div style={{ marginLeft: "20px", marginBottom: "20px" }}>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● Account notifications and security alerts
          </p>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● Order confirmations and transaction updates
          </p>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: "1.8",
              color: "#444",
              marginBottom: "10px",
            }}
          >
            ● Customer support communications
          </p>
          <p style={{ fontSize: "1rem", lineHeight: "1.8", color: "#444" }}>
            ● Promotional or marketing messages (if separately opted into)
          </p>
        </div>

        <p
          style={{
            fontSize: "1rem",
            lineHeight: "1.8",
            color: "#444",
            marginBottom: "10px",
            marginLeft: "20px",
          }}
        >
          Message frequency may vary. Standard message and data rates may apply
          depending on your carrier.
        </p>

        <p
          style={{
            fontSize: "1rem",
            lineHeight: "1.8",
            color: "#444",
            marginBottom: "20px",
            marginLeft: "20px",
          }}
        >
          You may opt out of receiving text messages at any time by following
          the instructions provided in the message (e.g., replying STOP). For
          help, reply HELP or contact us at contact@myhoom.com.
        </p>

        <Divider />

        <OrangeBullet title="9. Limitation of Liability">
          To the fullest extent permitted by law, my HOOM is not liable for any
          direct, indirect, incidental, or consequential damages arising out of
          your use of the Service or any transaction.
        </OrangeBullet>

        <OrangeBullet title="10. Termination">
          We reserve the right to suspend or terminate accounts for violation of
          these Terms or applicable laws, with or without notice.
        </OrangeBullet>

        <OrangeBullet title="11. Modifications">
          We may update these Terms at any time. Continued use of the Service
          after changes indicates your acceptance of the revised Terms.
        </OrangeBullet>

        <OrangeBullet title="12. Governing Law">
          These Terms are governed by the laws of the state of [Insert State],
          without regard to conflict-of-law principles.
        </OrangeBullet>

        <Divider />

        <OrangeBullet title="13. Contact Us">
          For questions or concerns, contact us at: contact@myhoom.com
        </OrangeBullet>

        <p style={{ marginTop: "40px", fontSize: "0.95rem", color: "#888" }}>
          Last updated: August 7, 2025
        </p>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#F9F4EA",
        width: "100%",
        // minHeight, not height, and no `overflow: hidden`. Together those two made anything
        // taller than the viewport unreachable — and because this row centres its content,
        // what did not fit spilled off BOTH edges with no way to scroll to either. On iOS the
        // collapsing URL bar makes 100vh taller than the visible area as well. AC-MOBILE-F2.
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          // The 400px logo and the card beside it total ~950px. Wrapping puts them on
          // separate centred lines below that width, which is the stacked phone layout —
          // no breakpoint, no state, one property. AC-MOBILE-F2.
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "1200px",
          gap: "clamp(24px, 6vw, 80px)",
          padding: "20px",
          boxSizing: "border-box",
        }}
      >
        {/* Left: MyHoom + Beer Icon */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <img
            src={beerIcon}
            alt="beer icon"
            style={{ width: "min(400px, 60vw)", height: "auto", cursor: "pointer" }}
            onClick={() => {
              if (signupOtpMode) {
                // 🔙 From Signup OTP → back to Enter Email
                setSignupOtpMode(false);
              } else {
                // 🔙 Exit Sign Up → navigate to login
                navigate("/admin-login");
              }
            }}
          />
        </div>

        {/* Right: Form */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              // min(..., 100%): both values are wider than a phone, and the card is a flex
              // item that would otherwise push its contents off both edges. AC-MOBILE-F2.
              width: signupOtpMode
                ? "min(540px, 100%)" // Signup OTP screen
                : "min(472px, 100%)", // Enter Email Address screen

              // minHeight below already carries the OTP screen's floor; a fixed height here
              // clipped the card once its contents wrapped on a narrow screen.
              height: "auto",
              minHeight: signupOtpMode ? "555px" : "345px",

              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              padding: "30px",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",

              transition: "all 0.3s ease",
            }}
          >
            {/* SIGNUP FLOW */}
            {signupOtpMode ? (
              // ✅ STEP 2: Enter your OTP Screen
              <div
                style={{ width: "100%", fontFamily: "Poppins, sans-serif" }}
              >
                {/* Title */}
                <h2
                  style={{
                    textAlign: "center",
                    width: "100%",
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: "#1E344D",
                    marginBottom: "16px",
                  }}
                >
                  Admin Panel
                </h2>
                {/* Subtitle + description */}
                <div style={{ marginBottom: "16px" }}>
                  <h3
                    style={{
                      fontSize: "32px",
                      fontWeight: "bold",
                      color: "#1E344D",
                      marginBottom: "16px",
                    }}
                  >
                    Enter your OTP
                  </h3>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "black",
                      lineHeight: "1.6",
                    }}
                  >
                    Enter the 6-digit code we just sent to{" "}
                    <span style={{ fontWeight: "600" }}>{email}</span>{" "}
                    <span
                      style={{
                        marginTop: "15px",
                        fontWeight: "bold",
                        color: "#DE810E",
                        cursor: "pointer",
                        marginLeft: "6px",
                      }}
                      onClick={() => {
                        setSignupOtpMode(false);
                        setSignupOtp(["", "", "", "", "", ""]);
                      }}
                    >
                      Edit
                    </span>
                  </p>
                </div>
                {/* OTP Inputs */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    // 6 boxes at 52px plus 5 gaps at 17px need 397px; the card's content box
                    // on a 390px screen is 330. Both clamp down together so the row still
                    // fits without wrapping, and keep their exact desktop values above
                    // ~450px. AC-MOBILE-F11.
                    gap: "clamp(6px, 2vw, 17px)",
                    marginBottom: "16px",
                  }}
                >
                  {signupOtp.map((digit, i) => (
                    <input
                      key={i}
                      maxLength={1}
                      value={digit}
                      ref={(el) => (signupInputRefs.current[i] = el)}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        const newOtp = [...signupOtp];
                        newOtp[i] = val;
                        setSignupOtp(newOtp);

                        // Auto move to next input
                        if (val && i < signupOtp.length - 1) {
                          signupInputRefs.current[i + 1]?.focus();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedData = e.clipboardData.getData("text");
                        const digits = pastedData
                          .replace(/\D/g, "")
                          .slice(0, 6); // Extract only digits, max 6

                        if (digits.length > 0) {
                          const newOtp = [...signupOtp];
                          // Always fill from the beginning (index 0) for better UX
                          for (
                            let j = 0;
                            j < digits.length && j < signupOtp.length;
                            j++
                          ) {
                            newOtp[j] = digits[j];
                          }
                          setSignupOtp(newOtp);

                          // Focus the last filled input or the last input if all filled
                          const lastFilledIndex = Math.min(
                            digits.length - 1,
                            signupOtp.length - 1,
                          );
                          setTimeout(() => {
                            signupInputRefs.current[lastFilledIndex]?.focus();
                          }, 0);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Handle backspace to move backward
                        if (e.key === "Backspace" && !signupOtp[i] && i > 0) {
                          signupInputRefs.current[i - 1]?.focus();
                        }
                      }}
                      style={{
                        width: "clamp(36px, 11.5vw, 52px)",
                        height: "clamp(36px, 11.5vw, 52px)",
                        flexShrink: 0,
                        borderRadius: "8px",
                        backgroundColor: "rgba(30, 52, 77, 0.1)",
                        border: "none",
                        textAlign: "center",
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "#1E344D",
                        outline: "none",
                        marginBottom: "40px",
                        marginTop: "40px",
                      }}
                    />
                  ))}
                </div>
                {/* Verify button */}
                <button
                  onClick={async () => {
                    const code = signupOtp.join("");
                    if (code.length < 6) {
                      toast.error("Please enter full 6-digit OTP.", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                      return;
                    }

                    try {
                      const data = await authApi.confirmOTP({
                        email: email,
                        otp: code,
                        password: tempPassword,
                      });

                      // Store tokens in localStorage if access_token exists
                      if (data.access_token) {
                        localStorage.setItem("userEmail", email);
                        localStorage.setItem("access_token", data.access_token);
                        if (data.id_token) {
                          localStorage.setItem("id_token", data.id_token);
                        }
                        if (data.refresh_token) {
                          localStorage.setItem(
                            "refresh_token",
                            data.refresh_token,
                          );
                        }
                        // Store temp_password for password change step
                        localStorage.setItem("temp_password", tempPassword);
                        // Seed the fixed session cap on this post-signup auto-login
                        // path too — it bypasses AuthContext.login() and writes tokens
                        // straight to localStorage, so the deadline must be set here.
                        tokenStore.beginSessionCap();
                        console.log(
                          "✅ [Signup] Tokens stored in localStorage",
                        );

                        toast.success("OTP verified successfully!", {
                          position: "top-right",
                          autoClose: 2000,
                        });
                        setTimeout(() => {
                          navigate("/create-password");
                        }, 500);
                      } else {
                        toast.error(
                          data.error ||
                            "OTP verified, but no access token received.",
                          {
                            position: "top-right",
                            autoClose: 4000,
                          },
                        );
                      }
                    } catch (err) {
                      console.error("🚨 [Signup OTP Verify] Error:", err);
                      toast.error(err.message || "Invalid OTP. Try again.", {
                        position: "top-right",
                        autoClose: 4000,
                      });
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "14px",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    background: "#DE810E",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    marginBottom: "40px",
                  }}
                >
                  Verify
                </button>
                {/* Resend OTP */}
                <p
                  style={{
                    fontSize: "14px",
                    color: "#1E344D",
                    textAlign: "center",
                    marginBottom: "0",
                  }}
                >
                  Didn't get a code?{" "}
                  <span
                    style={{
                      fontWeight: "bold",
                      color: "#DE810E",
                      cursor: signupResendCooldown ? "not-allowed" : "pointer",
                      opacity: signupResendCooldown ? 0.6 : 1,
                      pointerEvents: signupResendCooldown ? "none" : "auto",
                      transition: "opacity 0.3s ease",
                    }}
                    onClick={async () => {
                      if (signupResendCooldown) return;

                      try {
                        console.log(
                          "🟡 [Frontend] Sending resend signup OTP request...",
                        );
                        const data = await authApi.resendSignupOTP({ email });

                        if (data.error) {
                          toast.error(data.error, {
                            position: "top-right",
                            autoClose: 4000,
                          });
                          return;
                        }
                        toast.success("OTP resent successfully!", {
                          position: "top-right",
                          autoClose: 3000,
                        });
                        console.log(
                          "🕒 [Frontend] Restarting 60s cooldown after resend",
                        );

                        setSignupResendCooldown(true);
                        setSignupCooldownTime(60);

                        const interval = setInterval(() => {
                          setSignupCooldownTime((prev) => {
                            if (prev <= 1) {
                              clearInterval(interval);
                              setSignupResendCooldown(false);
                              console.log(
                                "✅ [Frontend] Signup cooldown finished — resend enabled",
                              );
                              return 0;
                            }
                            return prev - 1;
                          });
                        }, 1000);
                      } catch (err) {
                        console.error("🚨 [Frontend] Error:", err);
                        toast.error(err.message || "Failed to resend OTP.", {
                          position: "top-right",
                          autoClose: 4000,
                        });
                      }
                    }}
                  >
                    {signupResendCooldown
                      ? `Send again in ${signupCooldownTime}s`
                      : "Send again"}
                  </span>
                </p>
              </div>
            ) : (
              // ✅ STEP 1: Enter Email Address Screen
              <>
                <h2
                  style={{
                    textAlign: "center",
                    marginBottom: "30px",
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "black",
                  }}
                >
                  Enter Email Address
                </h2>

                <div style={{ marginBottom: "30px", position: "relative" }}>
                  <label style={{ fontSize: "14px", color: "black" }}>
                    Email Address<span style={{ color: "red" }}>*</span>
                  </label>

                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="operator@gmail.com"
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      marginTop: "5px",
                      boxSizing: "border-box",
                    }}
                  />

                  {isValid && (
                    <span
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "65%",
                        transform: "translateY(-40%)",
                        fontSize: "14px",
                      }}
                    >
                      <img
                        src={require("../assets/check.png")}
                        alt="valid"
                        style={{ width: "14px", height: "14px" }}
                      />
                    </span>
                  )}
                </div>

                {/* Legal Documents Checkboxes */}
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "12px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      style={{
                        marginRight: "8px",
                        marginTop: "2px",
                        cursor: "pointer",
                      }}
                    />
                    <label
                      style={{
                        fontSize: "14px",
                        color: "black",
                        cursor: "pointer",
                        lineHeight: "1.5",
                      }}
                    >
                      I agree to the{" "}
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          setShowTermsModal(true);
                        }}
                        style={{
                          color: "#DE810E",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        Terms of Service
                      </span>
                    </label>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={acceptPrivacy}
                      onChange={(e) => setAcceptPrivacy(e.target.checked)}
                      style={{
                        marginRight: "8px",
                        marginTop: "2px",
                        cursor: "pointer",
                      }}
                    />
                    <label
                      style={{
                        fontSize: "14px",
                        color: "black",
                        cursor: "pointer",
                        lineHeight: "1.5",
                      }}
                    >
                      I agree to the{" "}
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPrivacyModal(true);
                        }}
                        style={{
                          color: "#DE810E",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        Privacy Policy
                      </span>
                    </label>
                  </div>
                </div>

                {existingAccount === "unverified" && (
                  <div
                    role="alert"
                    style={{
                      background: "#FFF4E5",
                      border: "1px solid #FFD8A8",
                      borderRadius: "10px",
                      padding: "14px",
                      marginBottom: "16px",
                      textAlign: "left",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 10px",
                        fontSize: "13px",
                        lineHeight: 1.45,
                        color: "#8A5300",
                      }}
                    >
                      <strong>This email is already registered but not verified
                      yet.</strong> Enter the code we emailed you to finish — you don't
                      have to start over with a different address.
                    </p>
                    <VerifyEmailCta email={email} />
                  </div>
                )}

                <button
                  onClick={async (e) => {
                    e.preventDefault();

                    if (!email) {
                      toast.error("Please enter your email address.", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                      return;
                    }

                    if (!isValid) {
                      toast.error("Please enter a valid email address.", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                      return;
                    }

                    if (!acceptTerms || !acceptPrivacy) {
                      toast.error(
                        "Please accept both Terms of Service and Privacy Policy to continue.",
                        {
                          position: "top-right",
                          autoClose: 4000,
                        },
                      );
                      return;
                    }

                    const requestBody = {
                      email: email,
                    };
                    console.log("🟢 [Signup] Sending request:", requestBody);

                    try {
                      const data = await authApi.signup(requestBody);

                      // Check if the response contains an error field (API returns 200 OK with error in body)
                      if (data.error) {
                        console.error("🚨 [Signup] Error:", data.error);
                        // Only the unverified case gets an inline box (a link to
                        // /verify-email). A fully registered account ("already exists")
                        // is already covered by the "Already a partner? Log in" link
                        // below, so it shows the toast alone — no box.
                        const msg = String(data.error);
                        setExistingAccount(/verif/i.test(msg) ? "unverified" : null);
                        toast.error(msg || "Failed to send OTP.", {
                          position: "top-right",
                          autoClose: 4000,
                        });
                        return;
                      }
                      setExistingAccount(null);

                      setTempPassword(data.temp_password);
                      // Kept in React state only, and deliberately not persisted: it is a
                      // live credential. Losing it to a reload is no longer fatal — the user
                      // can finish at /verify-email, which needs only the emailed code.
                      toast.success("OTP sent to your email!", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                      console.log("✅ [Signup] OTP sent successfully");
                      setSignupOtpMode(true);
                    } catch (err) {
                      console.error("🚨 [Signup] Error:", err);
                      toast.error(
                        err.message || err.error || "Failed to send OTP.",
                        {
                          position: "top-right",
                          autoClose: 4000,
                        },
                      );
                    }
                  }}
                  disabled={!acceptTerms || !acceptPrivacy}
                  style={{
                    width: "100%",
                    padding: "14px",
                    borderRadius: "30px",
                    border: "none",
                    cursor:
                      acceptTerms && acceptPrivacy ? "pointer" : "not-allowed",
                    background:
                      acceptTerms && acceptPrivacy
                        ? "#DE810E"
                        : "rgba(222, 129, 14, 0.4)",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    opacity: acceptTerms && acceptPrivacy ? 1 : 0.6,
                    transition: "all 0.3s ease",
                  }}
                >
                  Send OTP
                </button>

                {/* Already a partner? Log in */}
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "16px",
                    color: "black",
                    marginTop: "20px",
                    marginBottom: "0",
                  }}
                >
                  Already a partner?{" "}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/admin-login")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/admin-login");
                      }
                    }}
                    style={{
                      fontWeight: "bold",
                      cursor: "pointer",
                      color: "#DE810E",
                    }}
                  >
                    Log in
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Legal Document Modals */}
      <LegalModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
      >
        <TermsContent />
      </LegalModal>

      <LegalModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
      >
        <PrivacyPolicyContent />
      </LegalModal>
    </div>
  );
}

export default AdminSignup;
